/**
 * Translation keys used by the overview UI. Kept here so any app that renders
 * the overview package (the user app and the admin app) can merge them into
 * its i18next instance. The user app already defines these keys in its own
 * `i18n.ts`; the admin app initialises i18next solely from this fragment.
 */

export const overviewI18nResources = {
  en: {
    // Team Overview
    "teamOverview.title": "Team Overview",
    "teamOverview.period.7d": "Recent 7 days",
    "teamOverview.period.30d": "Recent 30 days",
    "teamOverview.vsPrevious": "vs last period",
    "teamOverview.kpi.staleProjects": "Stale Projects",
    "teamOverview.kpi.createdThreads": "Created Threads",
    "teamOverview.kpi.completedThreads": "Completed Threads",
    "teamOverview.kpi.activeMembers": "Active members",
    "teamOverview.kpi.avgCompletion": "Avg. completion",
    "teamOverview.kpi.daysSuffix": "Days",
    "teamOverview.lists.mostActive": "Most Active Projects",
    "teamOverview.lists.threadsByProject": "Threads by Project",
    "teamOverview.lists.assigneesByProject": "Assignees per Projects",
    "teamOverview.lists.overdue": "Projects with Overdue Threads",
    "teamOverview.lists.delayRisk": "Projects with Delay Risk",
    "teamOverview.lists.delayCaution": "Projects with Delay Caution",
    "teamOverview.updatesToday": "{{count}} updates since yesterday",
    "teamOverview.viewMore": "View more",
    "teamOverview.threadCount": "{{count}} threads",
    "teamOverview.memberCount": "{{count}} members",
    "teamOverview.overdueBadge": "{{count}} overdue",
    "teamOverview.daysOverdue": "{{count}} days overdue",
    "teamOverview.dueInDays": "Due in {{count}} days",
    "teamOverview.dueToday": "Due today",
    "teamOverview.noDueDate": "No due date",
    "teamOverview.empty": "No data yet",

    // Project Overview
    "projectOverview.kpi.staleThreads": "Stale Threads",
    "projectOverview.kpi.createdThreads": "Created Threads",
    "projectOverview.kpi.completedThreads": "Completed Threads",
    "projectOverview.kpi.activeMembers": "Active members",
    "projectOverview.kpi.avgCompletion": "Avg. completion",
    "projectOverview.lists.avgCompletion": "Avg. completion",
    "projectOverview.lists.mostActiveThreads": "Most Active Threads",
    "projectOverview.lists.assigneeBreakdown": "Member Breakdown",
    "projectOverview.lists.overdue": "Overdue Threads",
    "projectOverview.lists.delayRisk": "Delay Risk",
    "projectOverview.lists.delayCaution": "Delay Caution",
    "projectOverview.daysSuffix": "days",
    "projectOverview.subtaskCount": "{{count}} sub-tasks",
    "projectOverview.unassigned": "Unassigned",
    "projectOverview.threadProgress": "{{members}} members involved",

    // Assignee Overview
    "assigneeOverview.kpi.involvedProjects": "Involved Projects",
    "assigneeOverview.kpi.assignedThreads": "Involved Threads",
    "assigneeOverview.kpi.completedThreads": "Completed Threads",
    "assigneeOverview.kpi.avgCompletion": "Avg. completion",
    "assigneeOverview.kpi.overdue": "Overdue",
    "assigneeOverview.lists.avgCompletion": "Avg. completion",
    "assigneeOverview.lists.assignedThreads": "Involved Threads and Sub-tasks",
    "assigneeOverview.lists.projectInvolvement": "Project Involvement",
    "assigneeOverview.lists.overdue": "Overdue Threads",
    "assigneeOverview.lists.delayRisk": "Delay Risk",
    "assigneeOverview.lists.delayCaution": "Delay Caution",

    // Common
    "common.back": "Back",
    "common.expandNavigation": "Expand navigation",
  },
  ko: {
    // Team Overview
    "teamOverview.title": "팀 오버뷰",
    "teamOverview.period.7d": "최근 7일",
    "teamOverview.period.30d": "최근 30일",
    "teamOverview.vsPrevious": "이전 기간 대비",
    "teamOverview.kpi.staleProjects": "정체된 프로젝트",
    "teamOverview.kpi.createdThreads": "생성된 스레드",
    "teamOverview.kpi.completedThreads": "완료된 스레드",
    "teamOverview.kpi.activeMembers": "활성 멤버",
    "teamOverview.kpi.avgCompletion": "평균 완료 기간",
    "teamOverview.kpi.daysSuffix": "일",
    "teamOverview.lists.mostActive": "가장 활발한 프로젝트",
    "teamOverview.lists.threadsByProject": "프로젝트별 스레드",
    "teamOverview.lists.assigneesByProject": "프로젝트별 담당자",
    "teamOverview.lists.overdue": "기한 초과 스레드가 있는 프로젝트",
    "teamOverview.lists.delayRisk": "지연 위험 프로젝트",
    "teamOverview.lists.delayCaution": "지연 주의 프로젝트",
    "teamOverview.updatesToday": "어제부터 {{count}}건 업데이트",
    "teamOverview.viewMore": "더보기",
    "teamOverview.threadCount": "스레드 {{count}}개",
    "teamOverview.memberCount": "멤버 {{count}}명",
    "teamOverview.overdueBadge": "지연 {{count}}",
    "teamOverview.daysOverdue": "{{count}}일 초과",
    "teamOverview.dueInDays": "{{count}}일 남음",
    "teamOverview.dueToday": "오늘 마감",
    "teamOverview.noDueDate": "마감일 없음",
    "teamOverview.empty": "데이터가 없습니다",

    // Project Overview
    "projectOverview.kpi.staleThreads": "정체된 스레드",
    "projectOverview.kpi.createdThreads": "생성된 스레드",
    "projectOverview.kpi.completedThreads": "완료된 스레드",
    "projectOverview.kpi.activeMembers": "활성 멤버",
    "projectOverview.kpi.avgCompletion": "평균 완료 기간",
    "projectOverview.lists.avgCompletion": "평균 완료 기간",
    "projectOverview.lists.mostActiveThreads": "가장 활발한 스레드",
    "projectOverview.lists.assigneeBreakdown": "멤버별 현황",
    "projectOverview.lists.overdue": "기한 초과 스레드",
    "projectOverview.lists.delayRisk": "지연 위험",
    "projectOverview.lists.delayCaution": "지연 주의",
    "projectOverview.daysSuffix": "일",
    "projectOverview.subtaskCount": "하위 업무 {{count}}개",
    "projectOverview.unassigned": "미할당",
    "projectOverview.threadProgress": "{{members}}명 참여",

    // Assignee Overview
    "assigneeOverview.kpi.involvedProjects": "참여 프로젝트",
    "assigneeOverview.kpi.assignedThreads": "참여 스레드",
    "assigneeOverview.kpi.completedThreads": "완료한 스레드",
    "assigneeOverview.kpi.avgCompletion": "평균 완료 기간",
    "assigneeOverview.kpi.overdue": "기한 초과",
    "assigneeOverview.lists.avgCompletion": "평균 완료 기간",
    "assigneeOverview.lists.assignedThreads": "참여 스레드 및 하위 업무",
    "assigneeOverview.lists.projectInvolvement": "프로젝트별 참여 현황",
    "assigneeOverview.lists.overdue": "기한 초과 스레드",
    "assigneeOverview.lists.delayRisk": "지연 위험",
    "assigneeOverview.lists.delayCaution": "지연 주의",

    // Common
    "common.back": "뒤로",
    "common.expandNavigation": "내비게이션 펼치기",
  },
} as const;
